import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';

import TickerIcon from './TickerIcon';
import { getSearchDestinations } from '../../constants/searchDestinations';
import {
  getCompanyInfo,
  getCompanyType,
  getCompanyTypeRowSx,
  isPrivateCompanyIdentifier,
  normalizeCompanyType,
} from '../../utils/companyHelpers';

function CompanySearchOptionRow({
  option,
  theme,
  component = 'li',
  expandable = false,
  onNavigate,
  sx,
  ...props
}) {
  const {
    style: optionStyle,
    onBlur: optionBlur,
    onFocus: optionFocus,
    onMouseEnter: optionMouseEnter,
    onMouseLeave: optionMouseLeave,
    onPointerEnter: optionPointerEnter,
    onPointerLeave: optionPointerLeave,
    ...optionProps
  } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const rowRef = useRef(null);
  const lastPointerType = useRef(null);
  const isTouchDevice = useMediaQuery('(hover: none)');
  const companyInfo = useMemo(() => getCompanyInfo(option.ticker), [option.ticker]);
  const showTicker = !isPrivateCompanyIdentifier(option.ticker);
  const companyType = useMemo(
    () => normalizeCompanyType(option.companyType || getCompanyType(companyInfo.raw)),
    [companyInfo.raw, option.companyType]
  );
  const rowSx = useMemo(() => getCompanyTypeRowSx(companyType, theme), [companyType, theme]);
  const destinations = useMemo(
    () => (expandable ? getSearchDestinations(option.ticker) : []),
    [expandable, option.ticker]
  );

  const expand = (event) => {
    if (!expandable || event?.pointerType === 'touch') return;
    if (event?.type?.startsWith('mouse') && lastPointerType.current === 'touch') return;
    setIsExpanded(true);
  };

  const collapse = (event) => {
    if (!expandable || event?.pointerType === 'touch') return;
    if (event?.type?.startsWith('mouse') && lastPointerType.current === 'touch') return;
    setIsExpanded(false);
  };

  const handlePointerEnter = (event) => {
    optionPointerEnter?.(event);
    lastPointerType.current = event.pointerType;
    expand(event);
  };

  const handlePointerLeave = (event) => {
    optionPointerLeave?.(event);
    lastPointerType.current = event.pointerType;
    collapse(event);
  };

  const handleCompanyClick = (event) => {
    if (!expandable) return;
    if ((isTouchDevice || lastPointerType.current === 'touch') && !isExpanded) {
      event.preventDefault();
      event.stopPropagation();
      setIsExpanded(true);
    }
  };

  const handleFocus = (event) => {
    optionFocus?.(event);
    if (expandable) setIsExpanded(true);
  };

  const handleBlur = (event) => {
    optionBlur?.(event);
    if (!rowRef.current?.contains(event.relatedTarget)) setIsExpanded(false);
  };

  const handleDestinationClick = (event, destination) => {
    event.preventDefault();
    event.stopPropagation();
    onNavigate?.(option.ticker, destination.id, option);
  };

  return (
    <Box
      ref={rowRef}
      component={component}
      {...optionProps}
      style={{
        ...optionStyle,
        ...(expandable ? {
          alignItems: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          listStyle: 'none',
          width: '100%',
        } : {}),
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={(event) => {
        optionMouseEnter?.(event);
        expand(event);
      }}
      onMouseLeave={(event) => {
        optionMouseLeave?.(event);
        collapse(event);
      }}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      sx={{
        width: '100%',
        minWidth: 0,
        ...(expandable ? {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        } : {}),
        border: 0,
        textAlign: 'left',
        font: 'inherit',
        cursor: 'pointer',
        listStyle: 'none',
        ...rowSx,
        ...sx,
      }}
    >
      <Box
        onClick={handleCompanyClick}
        sx={{
          width: '100%',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: expandable ? (isExpanded ? 0.85 : 0.55) : 0.35,
          fontSize: '12px',
        }}
      >
        <TickerIcon
          ticker={option.ticker}
          size={expandable ? 17 : 15}
          sourcePriority="cdn-first"
          style={{ opacity: 0.8, marginRight: 1, flexShrink: 0 }}
        />
        {expandable ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
            <Box
              component="span"
              sx={{
                color: theme.palette.primary.main,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {option.companyName}
            </Box>
            {showTicker && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', flexShrink: 0, fontSize: '11px' }}
              >
                · {option.ticker}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontSize: 12,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {option.companyName}
            </Typography>
            {showTicker && (
              <Typography sx={{ fontSize: 10, color: 'text.secondary', ml: 'auto', flexShrink: 0 }}>
                {option.ticker}
              </Typography>
            )}
          </>
        )}
      </Box>
      {expandable && (
        <Collapse in={isExpanded} timeout={150} sx={{ width: '100%' }}>
          <Box
            sx={{
              boxSizing: 'border-box',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
              width: '100%',
              gap: 0.75,
              px: 1.5,
              pb: 0.7,
            }}
          >
            {destinations.map((destination) => (
              <Tooltip key={destination.id} title={destination.label} placement="bottom" arrow>
                <IconButton
                  size="small"
                  aria-label={destination.label}
                  onClick={(event) => handleDestinationClick(event, destination)}
                  sx={{
                    color: theme.palette.text.secondary,
                    backgroundColor: theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.04)'
                      : 'rgba(255, 255, 255, 0.08)',
                    border: theme.palette.mode === 'light'
                      ? '1px solid rgba(0, 0, 0, 0.12)'
                      : '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '4px',
                    p: 0.6,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.12)'
                        : 'rgba(255, 255, 255, 0.16)',
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <destination.Icon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

export default CompanySearchOptionRow;
